import { useMemo } from 'react';

import {
  type FireflyTertiaryButtonMenuItem,
  ChakraBox,
  ChakraText,
  DatePickerInput,
  FAIconName,
  FireflyButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import type { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import { NotesSelect } from '~/components/maud/patients/note/NotesSelect';
import type { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import {
  NO_NOTE_TEMPLATE_OPTION,
  NOTE_ATTACHMENT_SOURCES,
} from '~/lib/constants/notes';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import type { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { toMultiSelectOption } from '~/lib/util/menu';
import {
  useGetAdminNoteActivityWithNamesEnumQuery,
  useGetAdminNoteTypesEnumQuery,
  useGetNoteCommunicationMethodsEnumQuery,
} from '~/store/services/enumerations';
import { useLazyGetNoteTemplateAutopopulateDataQuery } from '~/store/services/notes/notes';
import NoteAttachmentMenu from './NoteAttachmentMenu';

type Props = {
  id: string;
  activityWithOptions: NoteMultiSelectOption[];
  disableField: boolean;
  patientId: string;
  fieldErrorReducer: FieldErrorState;
  formData: EditNoteFormData;
  isGroupClassNote: boolean;
  isNoteAuthor: boolean;
  isAddendumModal: boolean;
  onFormChange: () => void;
  onUploadSuccess?: (docs: AttachmentType[]) => void;
  noteValueProcessing: boolean;
  setFieldValue: (
    key: keyof EditNoteFormData,
    value:
      | boolean
      | string
      | Date
      | NoteMultiSelectOption
      | Array<NoteMultiSelectOption>
      | FireflyTertiaryButtonMenuItem
      // @TODO Resolves eslint error caused by calling setFieldValue using data returned by getNoteTemplateAutopopulateData. Should this be allowed?
      | Record<string, unknown>,
  ) => void;
  updateFieldErrorReducer: (param: {
    dateError?: string | null;
    titleError?: string | null;
    typeError?: string | null;
  }) => void;
};
export const EditNoteFormAttributesRow = ({
  id,
  activityWithOptions,
  disableField,
  fieldErrorReducer,
  formData,
  isGroupClassNote,
  isNoteAuthor,
  isAddendumModal,
  onFormChange,
  onUploadSuccess,
  noteValueProcessing,
  patientId,
  setFieldValue,
  updateFieldErrorReducer,
}: Props) => {
  const { activityDate, noteTemplateRevision, noteTemplateRevisionData } =
    formData;
  const {
    currentUser: { userId: currentUserId },
  } = useGlobalContext();
  const { data: adminNoteTypes, isLoading: isGetAdminNoteTypesLoading } =
    useGetAdminNoteTypesEnumQuery();
  const {
    data: noteCommunicationMethods,
    isLoading: isGetNoteCommunicationMethodsLoading,
  } = useGetNoteCommunicationMethodsEnumQuery();

  const { isLoading: isGetAdminNoteActivityWithNamesLoading } =
    useGetAdminNoteActivityWithNamesEnumQuery();

  const [
    getNoteTemplateAutopopulateData,
    { isLoading: isGetNoteTemplateAutopopulateDataLoading },
  ] = useLazyGetNoteTemplateAutopopulateDataQuery();

  const activityMediumReasons = useMemo(
    () => (noteCommunicationMethods ?? []).map(toMultiSelectOption),
    [noteCommunicationMethods],
  );

  const adminNoteTypeOptions = useMemo(
    () => (adminNoteTypes || []).map(toMultiSelectOption),
    [adminNoteTypes],
  );

  // the appointment date can be in future, so take the max date based on the activityDate
  const maxDate = useMemo(
    () =>
      formData.activityDate > DateTime.now().toJSDate()
        ? formData.activityDate
        : DateTime.now().toJSDate(),
    [formData?.activityDate],
  );

  const canShowNoteAttachmentMenu =
    !isGroupClassNote &&
    ((!isAddendumModal && isNoteAuthor) || (isAddendumModal && !disableField));

  return (
    <ChakraBox
      alignItems="center"
      display="flex"
      gap="10px"
      marginBottom="12px"
    >
      <NotesSelect
        ariaLabel="Note type"
        error={fieldErrorReducer.typeError}
        fieldName="type"
        isDisabled={
          disableField || isGetAdminNoteTypesLoading || isGroupClassNote
        }
        isLoading={isGetAdminNoteTypesLoading}
        isRequired
        onFormChange={onFormChange}
        options={adminNoteTypeOptions ?? []}
        placeholder="Note type (required)"
        setFieldValue={setFieldValue}
        updateFieldErrorReducer={updateFieldErrorReducer}
        values={formData}
      />
      <ChakraText fontSize="sm">via</ChakraText>
      <NotesSelect
        ariaLabel="Activity medium"
        fieldName="medium"
        isDisabled={disableField || isGetNoteCommunicationMethodsLoading}
        isLoading={isGetNoteCommunicationMethodsLoading}
        minWidth="150px"
        onFormChange={onFormChange}
        options={activityMediumReasons ?? []}
        placeholder="medium"
        setFieldValue={setFieldValue}
        values={formData}
      />
      <ChakraText fontSize="sm">on</ChakraText>
      <DatePickerInput
        defaultDate={activityDate ? activityDate : undefined}
        id={`${id}__activity-date`}
        inputVariant="slim"
        isInvalid={Boolean(fieldErrorReducer.dateError)}
        isReadOnly={disableField}
        maxDate={maxDate}
        onSubmit={(date: Date) => {
          setFieldValue('activityDate', date);
          updateFieldErrorReducer({ dateError: null });
        }}
        placeholder="date (required)"
        rightIcon={FAIconName.chevronDownRegular}
        showTimeInput
      />
      <ChakraText fontSize="sm">with</ChakraText>
      <NotesSelect
        ariaLabel="Activity with"
        fieldName="activity"
        isDisabled={disableField || isGetAdminNoteActivityWithNamesLoading}
        isLoading={
          isGetAdminNoteActivityWithNamesLoading || noteValueProcessing
        }
        minWidth="150px"
        multiple
        onFormChange={onFormChange}
        options={activityWithOptions ?? []}
        placeholder="participants"
        setFieldValue={setFieldValue}
        values={formData}
      />
      {canShowNoteAttachmentMenu && (
        <NoteAttachmentMenu
          fromEditNoteForm
          id={id}
          source={NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL}
          onUploadSuccess={onUploadSuccess}
          patientId={patientId}
        />
      )}
      {noteTemplateRevision?.node &&
        noteTemplateRevision?.node !== NO_NOTE_TEMPLATE_OPTION.node &&
        !isAddendumModal && (
          <FireflyButton
            id={`${id}-refresh-note-template-data`}
            isLoading={isGetNoteTemplateAutopopulateDataLoading}
            leftIcon={FAIconName.refreshRegular}
            onClick={() => {
              getNoteTemplateAutopopulateData({
                patientId,
                providerId: currentUserId,
                templateRevisionId: noteTemplateRevision?.node,
                chartOnly: true,
              }).then((result) => {
                setFieldValue('noteTemplateRevisionData', {
                  ...noteTemplateRevisionData,
                  ...result?.data,
                });
              });
            }}
            size="small"
            variant="tertiary"
          >
            Refresh data
          </FireflyButton>
        )}
    </ChakraBox>
  );
};
